import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { navigation, type NavItem } from './navigation';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const linkClass = (isActive: boolean) =>
  clsx(
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    isActive
      ? 'bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300'
      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
  );

const Sidebar = ({ open, onClose }: SidebarProps) => {
  const { pathname } = useLocation();

  const isChildActive = (item: NavItem) =>
    item.children?.some((child) => pathname === child.path) ?? false;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/40 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-200 dark:border-gray-800 dark:bg-gray-900 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-6 dark:border-gray-800">
          <img src='/opentri-color.png' alt='Opentri Admin' title='Opentri Admin' width={30} />
          <span className="text-xl text-gray-800 dark:text-gray-200 font-bold">
            OpenTriAdmin
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="flex flex-col gap-1">
            {navigation.map((item) =>
              item.children?.length ? (
                <SidebarGroup
                  key={item.path}
                  item={item}
                  defaultOpen={isChildActive(item)}
                  onClose={onClose}
                />
              ) : (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onClose}
                    className={({ isActive }) => linkClass(isActive)}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>
      </aside>
    </>
  );
};

interface SidebarGroupProps {
  item: NavItem;
  defaultOpen: boolean;
  onClose: () => void;
}

const SidebarGroup = ({ item, defaultOpen, onClose }: SidebarGroupProps) => {
  const [expanded, setExpanded] = useState(defaultOpen);

  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className={clsx(linkClass(false), 'w-full')}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDownIcon
          className={clsx('h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-180')}
        />
      </button>
      {expanded && (
        <ul className="mt-1 flex flex-col gap-1">
          {item.children?.map((child) => (
            <li key={child.path}>
              <NavLink
                to={child.path}
                end
                onClick={onClose}
                className={({ isActive }) => clsx(linkClass(isActive), 'pl-11')}
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default Sidebar;
